/**
 * Pure data: the canonical list of custom-made product families (solution
 * subtypes) and their wizard card metadata. Split out of
 * SolutionSubtypePicker.tsx so non-component code (productLabel's canonical
 * label map, unit tests) can import it without dragging in the picker's
 * runtime dependencies (supabase client via useCardVisibilityFilter).
 * NO imports allowed here beyond types; keep this module side-effect free.
 */

export type SolutionSubtype =
  | 'pa12_insole'
  | 'tpu_insole'
  | 'polypropylene_insole'
  | 'carbon_insole'
  | 'eva_insole'
  | 'fibreglass_insole'
  | 'orthopaedic_sandal'
  | 'insoles_refurbishment';

export interface SubtypeOption {
  id: SolutionSubtype;
  /** One or more image URLs. >1 triggers a 3-second crossfade carousel. */
  images: string[];
  /** Existing SVG silhouette shown if a photo 404s before upload. */
  fallback: string;
  /** i18n keys for subtype name + description. */
  nameKey: string;
  descKey: string;
  /** Display string for expected delivery. Free text, not i18n'd yet. */
  delivery: string;
}

export const SUBTYPES: ReadonlyArray<SubtypeOption> = [
  // Row 1 - fast-turn 3D-printed + structural insoles
  {
    id: 'pa12_insole',
    images: ['/products/pa12-front-back.png', '/products/pa12-left.png'],
    fallback: '/products/ots-placeholder.svg',
    nameKey: 'subtype_pa12_name',
    descKey: 'subtype_pa12_desc',
    delivery: '5 working days',
  },
  {
    id: 'tpu_insole',
    images: ['/products/tpu.png'],
    fallback: '/products/ots-placeholder.svg',
    nameKey: 'subtype_tpu_name',
    descKey: 'subtype_tpu_desc',
    delivery: '5 working days',
  },
  {
    id: 'polypropylene_insole',
    images: [
      '/products/polypropylene-side.jpg',
      '/products/polypropylene-front-back.jpg',
      '/products/polypropylene-left.jpg',
      '/products/polypropylene-right.jpg',
    ],
    fallback: '/products/ots-placeholder.svg',
    nameKey: 'subtype_polypropylene_name',
    descKey: 'subtype_polypropylene_desc',
    delivery: '7-10 working days',
  },
  {
    id: 'carbon_insole',
    images: [
      '/products/carbon-side.jpg',
      '/products/carbon-front-back.jpg',
      '/products/carbon-left.jpg',
      '/products/carbon-right.jpg',
    ],
    fallback: '/products/ots-placeholder.svg',
    nameKey: 'subtype_carbon_name',
    descKey: 'subtype_carbon_desc',
    delivery: '7-10 working days',
  },
  // Row 2 - cushioning + sandals
  {
    id: 'eva_insole',
    images: [
      '/products/eva-side.jpg',
      '/products/eva-front-back.jpg',
      '/products/eva-left.jpg',
      '/products/eva-right.jpg',
    ],
    fallback: '/products/ots-placeholder.svg',
    nameKey: 'subtype_eva_name',
    descKey: 'subtype_eva_desc',
    delivery: '7-10 working days',
  },
  {
    id: 'fibreglass_insole',
    images: [
      '/products/fibreglass-side.jpg',
      '/products/fibreglass-front-back.jpg',
      '/products/fibreglass-left.jpg',
      '/products/fibreglass-right.jpg',
    ],
    fallback: '/products/ots-placeholder.svg',
    nameKey: 'subtype_fibreglass_name',
    descKey: 'subtype_fibreglass_desc',
    delivery: '7-10 working days',
  },
  {
    id: 'orthopaedic_sandal',
    images: [
      '/products/sandal-buckle-arena.png',
      '/products/sandal-buckle-caqui.png',
      '/products/sandal-velcro-black.png',
      '/products/sandal-buckle-mustard.png',
    ],
    fallback: '/products/ots-placeholder.svg',
    nameKey: 'subtype_sandal_name',
    descKey: 'subtype_sandal_desc',
    delivery: '10-14 working days',
  },
  // R35 — Insoles Refurbishment. For now reuses the existing
  // custom-insole prescription form. Final photo wired in
  // (insoles-refurbishment.png, 2026-06). Refurbishment is faster
  // than a fresh build because the shell is reused, so the SLA is
  // 2-3 working days vs 5-7 for new structural insoles.
  {
    id: 'insoles_refurbishment',
    images: ['/products/insoles-refurbishment.png'],
    fallback: '/products/ots-placeholder.svg',
    nameKey: 'subtype_refurbishment_name',
    descKey: 'subtype_refurbishment_desc',
    delivery: '2-3 working days',
  },
];
